import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { merge, ThemeUIProvider } from 'theme-ui';
import { lightTheme } from '@revdotcom/vaporwave-theme';
import {
    LoginFlow,
    type LoginProps,
    LogoutFlow,
    type LogoutProps,
    LandingFlow,
    type LandingProps,
    PasswordFlow,
    type PasswordProps,
    IdentityProvider,
} from '@rev-security/identity';

// app.css is embedded as styles by style-loader.
// Note importsNotUsedAsValues in tsconfig.settings and discussion
// in this issue: https://github.com/microsoft/TypeScript/issues/9191
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import css from '../assets/css/app.css';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import favicon from '../assets/images/favicon-32x32.png';

import theme from './theme';
import { Logo } from './components/Logo';

/**
 * Combined props for all flows.
 */
type Props = LoginProps | LogoutProps;

const App = (args: Props) => {
    const themeProps = { cardLogo: <Logo /> };

    return (
        <ThemeUIProvider theme={merge(theme, lightTheme)}>
            <IdentityProvider>
                <BrowserRouter>
                    <main sx={{ variant: 'cards.outer' }}>
                        <Routes>
                            <Route
                                path="/login/*"
                                element={
                                    <LoginFlow
                                        {...{
                                            ...(args as LoginProps),
                                            ...themeProps,
                                        }}
                                    />
                                }
                            />
                            <Route
                                path="/logout/*"
                                element={
                                    <LogoutFlow
                                        {...{
                                            ...(args as LogoutProps),
                                            ...themeProps,
                                        }}
                                    />
                                }
                            />
                            <Route
                                path="/landing/*"
                                element={
                                    <LandingFlow
                                        {...{
                                            ...(args as LandingProps),
                                            ...themeProps,
                                        }}
                                    />
                                }
                            />
                            <Route
                                path="/password/*"
                                element={
                                    <PasswordFlow
                                        {...{
                                            ...(args as PasswordProps),
                                            ...themeProps,
                                        }}
                                    />
                                }
                            />
                        </Routes>
                    </main>
                </BrowserRouter>
            </IdentityProvider>
        </ThemeUIProvider>
    );
};

/**
 * Bootstraps the application from the page.
 * @param {string} id DOM element id
 * @param {Props} args Application properties
 */
export default function (id: string, args: Props) {
    const root = document.getElementById(id);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    createRoot(root!).render(<App {...args} />);
}
